import type { ReactNode } from 'react';
import { IqName } from '@/components/IqName';
import { useQuery } from '@tanstack/react-query';
import { Icon, type IconName } from '@/lib/icons';
import { getClientTeam } from '@/data/client';
import { useIsAdmin } from '@/auth/useIsAdmin';
import { pickPitchTeam, pitchContact } from '@/lib/pitchTeam';

/* The screen a client sees on a module Triton hasn't switched on for them.
   A pitch, not a setup note: what they would get, the people who would run
   it, and who to ask. The setup note is still here for admins only. */
export function ServicePitch({ icon, service, headline, benefits, roles, ask, adminHint, aside }: {
  icon: IconName;
  service: string;
  headline: string;
  benefits: string[];
  /** Team roles to feature, in order; the Account Manager, when assigned, is who the card says to ask. */
  roles: string[];
  /** What to ask about, completing "Ask <name>, your <role>, …". */
  ask: string;
  adminHint?: ReactNode;
  /** Replaces the team column — a live preview, for a module that can show itself. */
  aside?: ReactNode;
}) {
  const isAdmin = useIsAdmin();
  const { data } = useQuery({ queryKey: ['clientTeam'], queryFn: getClientTeam, staleTime: 10 * 60_000 });
  const shown = pickPitchTeam(data?.team ?? [], roles);
  const contact = pitchContact(shown);

  return (
    <div className="dash-stack">
      <div className={`pitch ${aside ? 'pitch--wide' : ''}`}>
        <div className="pitch__main">
          <span className="pitch__icon"><Icon name={icon} size={26} /></span>
          <p className="pitch__kicker">{service} isn&rsquo;t switched on for your account yet</p>
          <h2 className="pitch__title">{headline}</h2>
          <ul className="pitch__benefits">
            {benefits.map((b) => (
              <li key={b}><Icon name="check" size={15} /><span>{b}</span></li>
            ))}
          </ul>
          <div className="pitch__cta">
            <p className="pitch__ask">
              <Icon name="message" size={16} />
              <span>
                {contact
                  ? <>Ask <strong>{contact.name}</strong>, your {contact.role.toLowerCase()}, {ask}</>
                  : <>Ask your Triton team {ask}</>}
              </span>
            </p>
          </div>
          {isAdmin && adminHint && <p className="pitch__admin"><Icon name="lock" size={13} /><span>{adminHint}</span></p>}
        </div>
        {aside ? <aside className="pitch__aside">{aside}</aside> : shown.length > 0 && (
          <aside className="pitch__team" aria-label="Your Triton team">
            <h3 className="pitch__team-title">The people behind it</h3>
            {shown.map((m) => (
              <div key={`${m.role}-${m.name}`} className="dash-team__member">
                {m.imageUrl
                  ? <img src={m.imageUrl} alt={m.name} className="dash-team__avatar pitch__avatar" />
                  : <div className="dash-team__avatar pitch__avatar dash-team__avatar--ph" aria-hidden="true">{m.name.charAt(0)}</div>}
                <div className="dash-team__meta">
                  <span className="dash-team__name">{m.name}</span>
                  <span className="dash-team__role"><IqName text={m.role} /></span>
                </div>
              </div>
            ))}
          </aside>
        )}
      </div>
    </div>
  );
}
